/**
 * Color contrast utilities for ensuring readable text on colored backgrounds
 */

/**
 * Calculate relative luminance of a color
 * @param {string} hex - Hex color string (with or without #)
 * @returns {number} Luminance value between 0 and 1
 */
function getLuminance(hex) {
  // Remove # if present
  const color = hex.replace('#', '');

  // Convert to RGB
  const r = parseInt(color.substr(0, 2), 16) / 255;
  const g = parseInt(color.substr(2, 2), 16) / 255;
  const b = parseInt(color.substr(4, 2), 16) / 255;

  // Apply gamma correction
  const gammaCorrect = (c) => {
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };

  // Calculate relative luminance
  return 0.2126 * gammaCorrect(r) + 0.7152 * gammaCorrect(g) + 0.0722 * gammaCorrect(b);
}

/**
 * Get contrast ratio between two colors
 * @param {string} color1 - First hex color
 * @param {string} color2 - Second hex color
 * @returns {number} Contrast ratio
 */
function getContrastRatio(color1, color2) {
  const lum1 = getLuminance(color1);
  const lum2 = getLuminance(color2);
  const lighter = Math.max(lum1, lum2);
  const darker = Math.min(lum1, lum2);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Get appropriate text color for a given background
 * @param {string} backgroundColor - Background hex color
 * @param {Object} options - Options for contrast
 * @param {string} options.lightColor - Light color option (default: '#212121' - very dark gray)
 * @param {string} options.darkColor - Dark color option (default: '#F5F5F5' - off-white)
 * @param {number} options.threshold - Luminance threshold (default: 0.5)
 * @returns {string} Appropriate text color
 */
export function getContrastText(backgroundColor, options = {}) {
  const {
    lightColor = '#212121', // Very dark gray for light backgrounds
    darkColor = '#F5F5F5',  // Off-white for dark backgrounds
    threshold = 0.5
  } = options;

  if (!backgroundColor || backgroundColor === 'transparent') {
    return lightColor;
  }

  const luminance = getLuminance(backgroundColor);

  // For mid-range colors, check actual contrast ratios
  if (luminance > 0.3 && luminance < 0.7) {
    const lightContrast = getContrastRatio(backgroundColor, lightColor);
    const darkContrast = getContrastRatio(backgroundColor, darkColor);

    // Use whichever provides better contrast (WCAG AA requires 4.5:1)
    return lightContrast > darkContrast ? lightColor : darkColor;
  }

  // For clearly light or dark backgrounds
  return luminance > threshold ? lightColor : darkColor;
}

/**
 * Convert hex color to rgba with opacity
 * @param {string} hex - Hex color
 * @param {number} opacity - Opacity value (0-1)
 * @returns {string} RGBA color string
 */
export function hexToRgba(hex, opacity) {
  const color = hex.replace('#', '');
  const r = parseInt(color.substr(0, 2), 16);
  const g = parseInt(color.substr(2, 2), 16);
  const b = parseInt(color.substr(4, 2), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

/**
 * Adjust color brightness
 * @param {string} hex - Hex color
 * @param {number} amount - Amount to lighten (positive) or darken (negative)
 * @returns {string} Adjusted hex color
 */
export function adjustBrightness(hex, amount) {
  const color = hex.replace('#', '');
  const num = parseInt(color, 16);
  const r = Math.max(0, Math.min(255, (num >> 16) + amount));
  const g = Math.max(0, Math.min(255, ((num >> 8) & 0x00FF) + amount));
  const b = Math.max(0, Math.min(255, (num & 0x0000FF) + amount));

  return '#' + [r, g, b].map(x => {
    const hex = x.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  }).join('');
}

/**
 * Get contrast color for Faana health scores
 * @param {number} healthScore - Health score (0-100)
 * @returns {string} Appropriate text color
 */
export function getFaanaContrastText(healthScore) {
  // Map health scores to background colors (from getFaanaHealthColor)
  let backgroundColor;
  if (healthScore >= 80) backgroundColor = '#4CAF50'; // Green
  else if (healthScore >= 60) backgroundColor = '#FBC02D'; // Yellow
  else if (healthScore >= 40) backgroundColor = '#FF9800'; // Orange
  else backgroundColor = '#D32F2F'; // Red

  return getContrastText(backgroundColor);
}